/**
 * Shared TTL cache + in-flight coalescing for reference-data GETs.
 *
 * Opening the Pro drawer fires ~20 API calls at once, and roughly half are
 * lookup lists that barely change (rails, scacs, locations, cst-dispatchers,
 * csrs, chassis size matrix) — refetched on every drawer open by every user,
 * all day. With ARR affinity pinning a user's whole burst to one instance,
 * the burst queues on its own lookups (measured 2026-08-25: a 30ms sibling
 * query spent 2.2s waiting behind the rest of the burst). Serving lookups
 * from this cache halves the burst and removes the standing load.
 *
 * Semantics:
 * - Success is cached for `ttlMs` (default 5 minutes) keyed by the full URL.
 * - Concurrent callers for the same URL share one request (coalescing).
 * - Failures are never cached — the next caller retries.
 * - A caller's AbortSignal only detaches THAT caller: the shared request
 *   keeps running so other consumers (and the cache) still get the data.
 *
 * The cached AxiosResponse (and its `data`) is shared between consumers by
 * reference — treat it as read-only (map/filter, don't mutate).
 */
import type { AxiosResponse } from 'axios';

import api from '../services/api';

const DEFAULT_TTL_MS = 5 * 60 * 1000;

type Entry = {
  promise: Promise<AxiosResponse<unknown>>;
  expiresAt: number;
};

const cache = new Map<string, Entry>();

/** Mirrors axios' CanceledError shape closely enough for existing catch blocks. */
class LookupCanceledError extends Error {
  name = 'CanceledError';
  code = 'ERR_CANCELED';

  constructor() {
    super('canceled');
  }
}

export function cachedGet<T>(
  url: string,
  options?: { ttlMs?: number; signal?: AbortSignal }
): Promise<AxiosResponse<T>> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const now = Date.now();

  let entry = cache.get(url);
  if (!entry || entry.expiresAt <= now) {
    // No caller signal on the underlying request: it is shared property.
    const promise = api.get<T>(url).catch((err: unknown) => {
      // Never cache a failure; only clear if this entry is still current.
      if (cache.get(url)?.promise === promise) {
        cache.delete(url);
      }
      throw err;
    }) as Promise<AxiosResponse<unknown>>;
    entry = { promise, expiresAt: now + ttlMs };
    cache.set(url, entry);
  }

  const shared = entry.promise as Promise<AxiosResponse<T>>;
  const signal = options?.signal;
  if (!signal) return shared;
  if (signal.aborted) return Promise.reject(new LookupCanceledError());

  // Detach just this caller when its signal aborts; the shared fetch lives on.
  return new Promise<AxiosResponse<T>>((resolve, reject) => {
    const onAbort = () => reject(new LookupCanceledError());
    signal.addEventListener('abort', onAbort, { once: true });
    shared.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      }
    );
  });
}

/** Tests / hard refresh flows. */
export function clearLookupCache(): void {
  cache.clear();
}
